import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams, useNavigate } from "react-router-dom";
import { AppDispatch, RootState } from "../../../../redux/store";
import { listenToShortstayData } from "../../../../redux/shortstaysSlice";
import { listenToShortGuestReservations } from "../../../../redux/shortstayReservationSlice";
import {
  X, Home, MapPin, LogIn, LogOut, Wifi, UtensilsCrossed,
  ShoppingBag, Navigation, HelpCircle, ArrowLeft, BookOpen,
  Eye, TreePine, MessageCircle, ExternalLink, Star, Loader,
  Car, Phone, AlertTriangle, Heart,
} from "lucide-react";

// ─── Types ───────────────────────────────────────────────────────────────────
interface ModalSection {
  id: string;
  label: string;
  icon: React.ReactNode;
  content: React.ReactNode;
}
interface NearbyPlace {
  name: string;
  rating?: number;
  address: string;
  lat: number;
  lng: number;
  placeId: string;
  photo?: string;
}
interface CountryInfo {
  emergency: string;
  police: string;
  ambulance: string;
  fire: string;
  countryName: string;
}

// ─── Constants ───────────────────────────────────────────────────────────────
const GOLD   = "#C8941F";
const CREAM  = "#FAF8F4";
const WHITE  = "#FFFFFF";
const DARK   = "#1C1410";
const MUTED  = "#9A8F85";
const BORDER = "#EDE8E0";
const RED    = "#DC2626";

// ─── Emergency numbers by country code ───────────────────────────────────────
// Source: Wikipedia/official government sources
const EMERGENCY_DB: Record<string, Omit<CountryInfo, "countryName">> = {
  "EE": { emergency: "112", police: "110", ambulance: "112", fire: "112" },  // Estonia
  "US": { emergency: "911", police: "911", ambulance: "911", fire: "911" },
  "GB": { emergency: "999", police: "999", ambulance: "999", fire: "999" },
  "AU": { emergency: "000", police: "000", ambulance: "000", fire: "000" },
  "CA": { emergency: "911", police: "911", ambulance: "911", fire: "911" },
  "DE": { emergency: "112", police: "110", ambulance: "112", fire: "112" },
  "FR": { emergency: "112", police: "17",  ambulance: "15",  fire: "18"  },
  "ES": { emergency: "112", police: "091", ambulance: "061", fire: "080" },
  "IT": { emergency: "112", police: "113", ambulance: "118", fire: "115" },
  "PT": { emergency: "112", police: "112", ambulance: "112", fire: "112" },
  "NL": { emergency: "112", police: "112", ambulance: "112", fire: "112" },
  "BE": { emergency: "112", police: "101", ambulance: "100", fire: "100" },
  "AT": { emergency: "112", police: "133", ambulance: "144", fire: "122" },
  "CH": { emergency: "112", police: "117", ambulance: "144", fire: "118" },
  "SE": { emergency: "112", police: "112", ambulance: "112", fire: "112" },
  "NO": { emergency: "112", police: "112", ambulance: "113", fire: "110" },
  "DK": { emergency: "112", police: "112", ambulance: "112", fire: "112" },
  "FI": { emergency: "112", police: "112", ambulance: "112", fire: "112" },
  "PL": { emergency: "112", police: "997", ambulance: "999", fire: "998" },
  "CZ": { emergency: "112", police: "158", ambulance: "155", fire: "150" },
  "GR": { emergency: "112", police: "100", ambulance: "166", fire: "199" },
  "HR": { emergency: "112", police: "192", ambulance: "194", fire: "193" },
  "TR": { emergency: "112", police: "155", ambulance: "112", fire: "110" },
  "AE": { emergency: "999", police: "999", ambulance: "998", fire: "997" },
  "IN": { emergency: "112", police: "100", ambulance: "108", fire: "101" },
  "JP": { emergency: "110", police: "110", ambulance: "119", fire: "119" },
  "CN": { emergency: "110", police: "110", ambulance: "120", fire: "119" },
  "KR": { emergency: "112", police: "112", ambulance: "119", fire: "119" },
  "BR": { emergency: "190", police: "190", ambulance: "192", fire: "193" },
  "MX": { emergency: "911", police: "911", ambulance: "911", fire: "911" },
  "ZA": { emergency: "10111", police: "10111", ambulance: "10177", fire: "10177" },
  "NG": { emergency: "199", police: "199", ambulance: "199", fire: "199" },
  "KE": { emergency: "999", police: "999", ambulance: "999", fire: "999" },
  "TH": { emergency: "191", police: "191", ambulance: "1669", fire: "199" },
  "ID": { emergency: "112", police: "110", ambulance: "118", fire: "113" },
  "MY": { emergency: "999", police: "999", ambulance: "999", fire: "994" },
  "SG": { emergency: "999", police: "999", ambulance: "995", fire: "995" },
  "NZ": { emergency: "111", police: "111", ambulance: "111", fire: "111" },
  "AR": { emergency: "911", police: "101", ambulance: "107", fire: "100" },
  "CL": { emergency: "133", police: "133", ambulance: "131", fire: "132" },
  "RU": { emergency: "112", police: "102", ambulance: "103", fire: "101" },
  "UA": { emergency: "112", police: "102", ambulance: "103", fire: "101" },
  "IL": { emergency: "100", police: "100", ambulance: "101", fire: "102" },
  "SA": { emergency: "999", police: "999", ambulance: "997", fire: "998" },
  "EG": { emergency: "123", police: "122", ambulance: "123", fire: "180" },
  "MA": { emergency: "15",  police: "19",  ambulance: "15",  fire: "15"  },
  "TZ": { emergency: "112", police: "112", ambulance: "112", fire: "112" },
  "GH": { emergency: "191", police: "191", ambulance: "193", fire: "192" },
  "LV": { emergency: "112", police: "110", ambulance: "113", fire: "112" },
  "LT": { emergency: "112", police: "112", ambulance: "112", fire: "112" },
  "SK": { emergency: "112", police: "158", ambulance: "155", fire: "150" },
  "HU": { emergency: "112", police: "107", ambulance: "104", fire: "105" },
  "RO": { emergency: "112", police: "112", ambulance: "112", fire: "112" },
  "BG": { emergency: "112", police: "166", ambulance: "150", fire: "160" },
  "RS": { emergency: "112", police: "192", ambulance: "194", fire: "193" },
  "IE": { emergency: "999", police: "999", ambulance: "999", fire: "999" },
  "PH": { emergency: "911", police: "911", ambulance: "911", fire: "911" },
  "VN": { emergency: "113", police: "113", ambulance: "115", fire: "114" },
  "PK": { emergency: "115", police: "15",  ambulance: "115", fire: "16"  },
  "BD": { emergency: "999", police: "999", ambulance: "999", fire: "999" },
  "LK": { emergency: "119", police: "119", ambulance: "110", fire: "111" },
  "NP": { emergency: "100", police: "100", ambulance: "102", fire: "101" },
  "DEFAULT": { emergency: "112", police: "112", ambulance: "112", fire: "112" },
};

// ─── Ride apps by country/region ─────────────────────────────────────────────
const RIDE_APPS: Record<string, { name: string; url: string; color: string }[]> = {
  "US": [
    { name: "Uber", url: "https://uber.com", color: "#000000" },
    { name: "Lyft", url: "https://lyft.com", color: "#FF00BF" },
  ],
  "GB": [
    { name: "Uber", url: "https://uber.com", color: "#000000" },
    { name: "Bolt", url: "https://bolt.eu", color: "#34D186" },
    { name: "Free Now", url: "https://free-now.com", color: "#FFDD00" },
  ],
  "EE": [
    { name: "Bolt", url: "https://bolt.eu", color: "#34D186" },
    { name: "Uber", url: "https://uber.com", color: "#000000" },
    { name: "Yandex Go", url: "https://taxi.yandex.com", color: "#FFCC00" },
  ],
  "LV": [
    { name: "Bolt", url: "https://bolt.eu", color: "#34D186" },
    { name: "Uber", url: "https://uber.com", color: "#000000" },
  ],
  "LT": [
    { name: "Bolt", url: "https://bolt.eu", color: "#34D186" },
    { name: "Uber", url: "https://uber.com", color: "#000000" },
  ],
  "DE": [
    { name: "Uber", url: "https://uber.com", color: "#000000" },
    { name: "Free Now", url: "https://free-now.com", color: "#FFDD00" },
    { name: "Bolt", url: "https://bolt.eu", color: "#34D186" },
  ],
  "FR": [
    { name: "Uber", url: "https://uber.com", color: "#000000" },
    { name: "Bolt", url: "https://bolt.eu", color: "#34D186" },
    { name: "Free Now", url: "https://free-now.com", color: "#FFDD00" },
  ],
  "ES": [
    { name: "Uber", url: "https://uber.com", color: "#000000" },
    { name: "Cabify", url: "https://cabify.com", color: "#5C1DCC" },
    { name: "Bolt", url: "https://bolt.eu", color: "#34D186" },
  ],
  "IT": [
    { name: "Uber", url: "https://uber.com", color: "#000000" },
    { name: "itTaxi", url: "https://www.ittaxi.it", color: "#E84118" },
    { name: "Free Now", url: "https://free-now.com", color: "#FFDD00" },
  ],
  "PT": [
    { name: "Uber", url: "https://uber.com", color: "#000000" },
    { name: "Bolt", url: "https://bolt.eu", color: "#34D186" },
    { name: "Free Now", url: "https://free-now.com", color: "#FFDD00" },
  ],
  "NL": [
    { name: "Uber", url: "https://uber.com", color: "#000000" },
    { name: "Bolt", url: "https://bolt.eu", color: "#34D186" },
  ],
  "SE": [
    { name: "Uber", url: "https://uber.com", color: "#000000" },
    { name: "Bolt", url: "https://bolt.eu", color: "#34D186" },
  ],
  "NO": [
    { name: "Uber", url: "https://uber.com", color: "#000000" },
    { name: "Bolt", url: "https://bolt.eu", color: "#34D186" },
  ],
  "DK": [
    { name: "Uber", url: "https://uber.com", color: "#000000" },
    { name: "Bolt", url: "https://bolt.eu", color: "#34D186" },
  ],
  "FI": [
    { name: "Uber", url: "https://uber.com", color: "#000000" },
    { name: "Bolt", url: "https://bolt.eu", color: "#34D186" },
    { name: "Yango", url: "https://yango.com", color: "#E43A00" },
  ],
  "TR": [
    { name: "Uber", url: "https://uber.com", color: "#000000" },
    { name: "BiTaksi", url: "https://bitaksi.com", color: "#FF6B35" },
    { name: "Bolt", url: "https://bolt.eu", color: "#34D186" },
  ],
  "AE": [
    { name: "Uber", url: "https://uber.com", color: "#000000" },
    { name: "Careem", url: "https://careem.com", color: "#3BB54A" },
  ],
  "IN": [
    { name: "Uber", url: "https://uber.com", color: "#000000" },
    { name: "Ola", url: "https://olacabs.com", color: "#F8C300" },
    { name: "Rapido", url: "https://rapido.bike", color: "#EE3441" },
  ],
  "JP": [
    { name: "Uber", url: "https://uber.com", color: "#000000" },
    { name: "GO", url: "https://go.mo-t.com", color: "#00A0E9" },
    { name: "DiDi", url: "https://didiglobal.com", color: "#FF6600" },
  ],
  "CN": [
    { name: "DiDi", url: "https://didiglobal.com", color: "#FF6600" },
    { name: "Shouqi", url: "https://shouqi.com.cn", color: "#0098D9" },
  ],
  "AU": [
    { name: "Uber", url: "https://uber.com", color: "#000000" },
    { name: "DiDi", url: "https://didiglobal.com", color: "#FF6600" },
    { name: "Ola", url: "https://olacabs.com.au", color: "#F8C300" },
  ],
  "BR": [
    { name: "Uber", url: "https://uber.com", color: "#000000" },
    { name: "99", url: "https://99app.com", color: "#F5A623" },
    { name: "inDrive", url: "https://indrive.com", color: "#44D62C" },
  ],
  "MX": [
    { name: "Uber", url: "https://uber.com", color: "#000000" },
    { name: "DiDi", url: "https://didiglobal.com", color: "#FF6600" },
    { name: "inDrive", url: "https://indrive.com", color: "#44D62C" },
  ],
  "ZA": [
    { name: "Uber", url: "https://uber.com", color: "#000000" },
    { name: "Bolt", url: "https://bolt.eu", color: "#34D186" },
    { name: "inDrive", url: "https://indrive.com", color: "#44D62C" },
  ],
  "KE": [
    { name: "Bolt", url: "https://bolt.eu", color: "#34D186" },
    { name: "Uber", url: "https://uber.com", color: "#000000" },
    { name: "Little", url: "https://little.bz", color: "#E84118" },
  ],
  "NG": [
    { name: "Bolt", url: "https://bolt.eu", color: "#34D186" },
    { name: "Uber", url: "https://uber.com", color: "#000000" },
    { name: "inDrive", url: "https://indrive.com", color: "#44D62C" },
  ],
  "TH": [
    { name: "Grab", url: "https://grab.com", color: "#00B14F" },
    { name: "Bolt", url: "https://bolt.eu", color: "#34D186" },
    { name: "inDrive", url: "https://indrive.com", color: "#44D62C" },
  ],
  "ID": [
    { name: "Gojek", url: "https://gojek.com", color: "#009900" },
    { name: "Grab", url: "https://grab.com", color: "#00B14F" },
  ],
  "MY": [
    { name: "Grab", url: "https://grab.com", color: "#00B14F" },
    { name: "AirAsia Ride", url: "https://ride.airasia.com", color: "#E21836" },
  ],
  "SG": [
    { name: "Grab", url: "https://grab.com", color: "#00B14F" },
    { name: "Gojek", url: "https://gojek.com", color: "#009900" },
    { name: "Tada", url: "https://tada.global", color: "#5E17EB" },
  ],
  "PH": [
    { name: "Grab", url: "https://grab.com", color: "#00B14F" },
    { name: "Angkas", url: "https://angkas.com", color: "#F7941D" },
  ],
  "VN": [
    { name: "Grab", url: "https://grab.com", color: "#00B14F" },
    { name: "Be", url: "https://be.com.vn", color: "#18BFFF" },
  ],
  "CA": [
    { name: "Uber", url: "https://uber.com", color: "#000000" },
    { name: "Lyft", url: "https://lyft.com", color: "#FF00BF" },
  ],
  "NZ": [
    { name: "Uber", url: "https://uber.com", color: "#000000" },
    { name: "Zoomy", url: "https://zoomy.co.nz", color: "#6400FF" },
  ],
  "DEFAULT": [
    { name: "Uber", url: "https://uber.com", color: "#000000" },
    { name: "Bolt", url: "https://bolt.eu", color: "#34D186" },
  ],
};

// ─── Reverse geocode to get country code ─────────────────────────────────────
const getCountryCode = (lat: number, lng: number): Promise<string> =>
  new Promise((resolve) => {
    if (!window.google?.maps) { resolve("DEFAULT"); return; }
    const geocoder = new window.google.maps.Geocoder();
    geocoder.geocode({ location: { lat, lng } }, (results: any[], status: string) => {
      if (status !== "OK" || !results?.length) { resolve("DEFAULT"); return; }
      const countryComp = results[0].address_components?.find((c: any) =>
        c.types.includes("country")
      );
      resolve(countryComp?.short_name || "DEFAULT");
    });
  });

// ─── Google Places fetch ──────────────────────────────────────────────────────
const fetchNearby = (lat: number, lng: number, type: string, keyword?: string): Promise<NearbyPlace[]> =>
  new Promise((resolve) => {
    if (!window.google?.maps?.places) { resolve([]); return; }
    const svc = new window.google.maps.places.PlacesService(document.createElement("div"));
    const req: any = { location: { lat, lng }, radius: 2000, type };
    if (keyword) req.keyword = keyword;
    svc.nearbySearch(req, (results: any[], status: string) => {
      if (status !== window.google.maps.places.PlacesServiceStatus.OK || !results) { resolve([]); return; }
      resolve(results.slice(0, 6).map((r: any) => ({
        name: r.name,
        rating: r.rating,
        address: r.vicinity,
        lat: r.geometry.location.lat(),
        lng: r.geometry.location.lng(),
        placeId: r.place_id,
        photo: r.photos?.[0]?.getUrl({ maxWidth: 120 }),
      })));
    });
  });

// ─── Map URL helpers ──────────────────────────────────────────────────────────
const openInGoogleMaps   = (lat: number, lng: number) => window.open(`https://www.google.com/maps/search/?api=1&query=${lat},${lng}`, "_blank");
const openDirections     = (lat: number, lng: number) => window.open(`https://www.google.com/maps/dir/?api=1&destination=${lat},${lng}`, "_blank");
const openNearbySearch   = (lat: number, lng: number, q: string) => window.open(`https://www.google.com/maps/search/${encodeURIComponent(q)}/@${lat},${lng},15z`, "_blank");

// ─── Sub-components ───────────────────────────────────────────────────────────
const GoldButton: React.FC<{ icon?: React.ReactNode; onClick: () => void; children: React.ReactNode }> = ({ icon, onClick, children }) => (
  <button style={S.goldBtn} onClick={onClick}>{icon}{children}<ExternalLink size={13} color={WHITE} /></button>
);

const KVList: React.FC<{ obj?: Record<string, any>; lat?: number; lng?: number }> = ({ obj, lat, lng }) => {
  if (!obj || !Object.keys(obj).length) return <p style={S.empty}>Not provided</p>;
  const entries = Object.entries(obj).filter(([k]) => k !== "latitude" && k !== "longitude");
  return (
    <div style={S.kvGrid}>
      {entries.map(([k, v]) => (
        <div key={k} style={S.kvRow}>
          <span style={S.kvKey}>{k}</span>
          <span style={S.kvVal}>{v != null ? String(v) : "—"}</span>
        </div>
      ))}
      {lat && lng && <GoldButton icon={<MapPin size={14} color={WHITE} />} onClick={() => openInGoogleMaps(lat, lng)}>Open in Google Maps</GoldButton>}
    </div>
  );
};

const ArrayDetail: React.FC<{ selected?: string[]; details?: Record<string, string> }> = ({ selected = [], details = {} }) => {
  if (!selected.length) return <p style={S.empty}>Not provided</p>;
  return (
    <div style={S.kvGrid}>
      {selected.map((k) => (
        <div key={k} style={S.kvRow}>
          <span style={S.kvKey}>{k}</span>
          <span style={S.kvVal}>{details[k] || "—"}</span>
        </div>
      ))}
    </div>
  );
};

// ─── Nearby panel ─────────────────────────────────────────────────────────────
const NearbyPanel: React.FC<{ lat: number; lng: number; type: string; label: string; keyword?: string }> = ({ lat, lng, type, label, keyword }) => {
  const [places, setPlaces] = useState<NearbyPlace[]>([]);
  const [busy, setBusy] = useState(true);

  useEffect(() => {
    setBusy(true);
    fetchNearby(lat, lng, type, keyword).then((p) => { setPlaces(p); setBusy(false); });
  }, [lat, lng, type, keyword]);

  if (busy) return (
    <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "20px 0", color: MUTED }}>
      <Loader size={17} color={GOLD} className="spin" />
      <span style={{ fontSize: 14, fontFamily: "'Poppins',sans-serif" }}>Finding nearby {label}…</span>
    </div>
  );

  if (!places.length) return (
    <>
      <p style={S.empty}>No {label} found nearby.</p>
      <GoldButton icon={<MapPin size={14} color={WHITE} />} onClick={() => openNearbySearch(lat, lng, label)}>Search on Google Maps</GoldButton>
    </>
  );

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 9 }}>
      {places.map((p) => (
        <div key={p.placeId} style={S.placeCard} onClick={() => openDirections(p.lat, p.lng)}>
          {p.photo
            ? <img src={p.photo} alt={p.name} style={S.placePhoto} />
            : <div style={S.placePhotoFallback}><MapPin size={18} color={GOLD} /></div>
          }
          <div style={S.placeInfo}>
            <div style={S.placeName}>{p.name}</div>
            <div style={S.placeAddr}>{p.address}</div>
            {p.rating && (
              <div style={S.placeRating}>
                <Star size={11} fill={GOLD} color={GOLD} /> {p.rating.toFixed(1)}
              </div>
            )}
          </div>
          <Navigation size={14} color={GOLD} style={{ flexShrink: 0 }} />
        </div>
      ))}
      <button style={S.outlineBtn} onClick={() => openNearbySearch(lat, lng, label)}>
        View all on Google Maps <ExternalLink size={12} />
      </button>
    </div>
  );
};

// ─── Emergency panel ──────────────────────────────────────────────────────────
const EmergencyPanel: React.FC<{ lat: number; lng: number }> = ({ lat, lng }) => {
  const [info, setInfo] = useState<CountryInfo | null>(null);
  const [hospitals, setHospitals] = useState<NearbyPlace[]>([]);
  const [busyGeo, setBusyGeo] = useState(true);
  const [busyHosp, setBusyHosp] = useState(true);

  useEffect(() => {
    getCountryCode(lat, lng).then((code) => {
      const data = EMERGENCY_DB[code] || EMERGENCY_DB["DEFAULT"];
      // Get country name via geocoder
      if (window.google?.maps) {
        const gc = new window.google.maps.Geocoder();
        gc.geocode({ location: { lat, lng } }, (results: any[], status: string) => {
          const comp = results?.[0]?.address_components?.find((c: any) => c.types.includes("country"));
          setInfo({ ...data, countryName: comp?.long_name || code });
          setBusyGeo(false);
        });
      } else {
        setInfo({ ...data, countryName: code });
        setBusyGeo(false);
      }
    });
    fetchNearby(lat, lng, "hospital").then((p) => { setHospitals(p); setBusyHosp(false); });
  }, [lat, lng]);

  if (busyGeo) return (
    <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "20px 0", color: MUTED }}>
      <Loader size={17} color={RED} className="spin" />
      <span style={{ fontSize: 14, fontFamily: "'Poppins',sans-serif" }}>Loading emergency info…</span>
    </div>
  );

  return (
    <div>
      {/* Emergency Numbers Card */}
      <div style={S.emergencyCard}>
        <div style={S.emergencyHeader}>
          <AlertTriangle size={20} color={RED} />
          <span style={S.emergencyCountry}>Emergency Numbers — {info?.countryName}</span>
        </div>
        <div style={S.emergencyGrid}>
          {[
            { label: "Emergency", number: info?.emergency, icon: "🆘" },
            { label: "Police",    number: info?.police,    icon: "🚔" },
            { label: "Ambulance", number: info?.ambulance, icon: "🚑" },
            { label: "Fire",      number: info?.fire,      icon: "🚒" },
          ].map((row) => (
            <a key={row.label} href={`tel:${row.number}`} style={S.emergencyItem}>
              <span style={S.emergencyEmoji}>{row.icon}</span>
              <div>
                <div style={S.emergencyLabel}>{row.label}</div>
                <div style={S.emergencyNumber}>{row.number}</div>
              </div>
              <Phone size={14} color={RED} style={{ marginLeft: "auto", flexShrink: 0 }} />
            </a>
          ))}
        </div>
      </div>

      {/* Nearby Hospitals */}
      <div style={{ marginTop: 20 }}>
        <div style={S.sectionSubHead}>
          <Heart size={15} color={RED} /> Nearby Hospitals
        </div>
        {busyHosp ? (
          <div style={{ display: "flex", alignItems: "center", gap: 8, color: MUTED, padding: "12px 0" }}>
            <Loader size={15} color={RED} className="spin" />
            <span style={{ fontSize: 13, fontFamily: "'Poppins',sans-serif" }}>Finding hospitals…</span>
          </div>
        ) : !hospitals.length ? (
          <p style={S.empty}>No hospitals found in 2 km.</p>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 8, marginTop: 8 }}>
            {hospitals.map((h) => (
              <div key={h.placeId} style={{ ...S.placeCard, borderColor: "#FCA5A5" }} onClick={() => openDirections(h.lat, h.lng)}>
                <div style={{ ...S.placePhotoFallback, background: "#FEF2F2" }}>
                  <Heart size={18} color={RED} />
                </div>
                <div style={S.placeInfo}>
                  <div style={S.placeName}>{h.name}</div>
                  <div style={S.placeAddr}>{h.address}</div>
                  {h.rating && <div style={S.placeRating}><Star size={11} fill={GOLD} color={GOLD} /> {h.rating.toFixed(1)}</div>}
                </div>
                <Navigation size={14} color={RED} style={{ flexShrink: 0 }} />
              </div>
            ))}
          </div>
        )}
        <button style={{ ...S.outlineBtn, marginTop: 8, borderColor: "#FCA5A5", color: RED }} onClick={() => openNearbySearch(lat, lng, "hospital")}>
          View all hospitals on Google Maps <ExternalLink size={12} />
        </button>
      </div>
    </div>
  );
};

// ─── Rides panel ─────────────────────────────────────────────────────────────
const RidesPanel: React.FC<{ lat: number; lng: number }> = ({ lat, lng }) => {
  const [apps, setApps] = useState<{ name: string; url: string; color: string }[]>([]);
  const [busy, setBusy] = useState(true);
  const [taxiStands, setTaxiStands] = useState<NearbyPlace[]>([]);

  useEffect(() => {
    getCountryCode(lat, lng).then((code) => {
      setApps(RIDE_APPS[code] || RIDE_APPS["DEFAULT"]);
      setBusy(false);
    });
    fetchNearby(lat, lng, "taxi_stand").then(setTaxiStands);
  }, [lat, lng]);

  if (busy) return (
    <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "20px 0", color: MUTED }}>
      <Loader size={17} color={GOLD} className="spin" />
      <span style={{ fontSize: 14, fontFamily: "'Poppins',sans-serif" }}>Detecting available ride apps…</span>
    </div>
  );

  return (
    <div>
      <p style={{ ...S.modalIntro, marginBottom: 16 }}>Ride apps available in this area:</p>
      <div style={S.appsGrid}>
        {apps.map((app) => (
          <a
            key={app.name}
            href={app.url}
            target="_blank"
            rel="noopener noreferrer"
            style={{ ...S.appCard, borderColor: app.color + "44" }}
          >
            <div style={{ ...S.appDot, background: app.color }} />
            <span style={S.appName}>{app.name}</span>
            <ExternalLink size={12} color={MUTED} />
          </a>
        ))}
      </div>

      {taxiStands.length > 0 && (
        <>
          <div style={{ ...S.sectionSubHead, marginTop: 20 }}>
            <Car size={15} color={GOLD} /> Nearby Taxi Stands
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 8, marginTop: 8 }}>
            {taxiStands.slice(0, 4).map((t) => (
              <div key={t.placeId} style={S.placeCard} onClick={() => openDirections(t.lat, t.lng)}>
                <div style={S.placePhotoFallback}><Car size={18} color={GOLD} /></div>
                <div style={S.placeInfo}>
                  <div style={S.placeName}>{t.name}</div>
                  <div style={S.placeAddr}>{t.address}</div>
                </div>
                <Navigation size={14} color={GOLD} style={{ flexShrink: 0 }} />
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
};

// ─── Main component ───────────────────────────────────────────────────────────
const ShortChecking: React.FC = () => {
  const { listingId } = useParams<{ listingId: string }>();
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();

  const currentUser       = useSelector((s: RootState) => s.auth.user);
  const currentListing    = useSelector((s: RootState) => s.shortstays.currentListing);
  const guestReservations = useSelector((s: RootState) => s.shortstayReservations.guestReservations);
  const allUsers          = useSelector((s: RootState) => s.auth.allUsers);

  const [loading, setLoading]         = useState(true);
  const [activeModal, setActiveModal] = useState<ModalSection | null>(null);
  const [isDesktop, setIsDesktop]     = useState(false);

  const lat    = Number(currentListing?.address?.latitude)  || 0;
  const lng    = Number(currentListing?.address?.longitude) || 0;
  const hasGeo = lat !== 0 && lng !== 0;

  useEffect(() => {
    const mq = window.matchMedia("(min-width:640px)");
    setIsDesktop(mq.matches);
    const h = (e: MediaQueryListEvent) => setIsDesktop(e.matches);
    mq.addEventListener("change", h);
    return () => mq.removeEventListener("change", h);
  }, []);

  useEffect(() => {
    if (!listingId || !currentUser) return;
    (async () => {
      setLoading(true);
      await dispatch(listenToShortstayData(listingId));
      await dispatch(listenToShortGuestReservations(currentUser.userId));
      setLoading(false);
    })();
  }, [dispatch, listingId, currentUser]);

  useEffect(() => {
    if (!listingId || !guestReservations.length) return;
    if (!guestReservations.some((r) => r.listingId === listingId)) navigate("/unauthorized");
  }, [guestReservations, listingId, navigate]);

  useEffect(() => {
    const h = (e: KeyboardEvent) => { if (e.key === "Escape") setActiveModal(null); };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, []);

  // ── All sections ───────────────────────────────────────────────────────────
  const sections: ModalSection[] = [
    {
      id: "welcome",
      label: "Welcome",
      icon: <Home size={24} strokeWidth={1.5} color={GOLD} />,
      content: (
        <>
          <p style={S.modalIntro}>Welcome to <strong>{currentListing?.title || "your stay"}</strong>! Tap any icon below to explore your stay and the surrounding area.</p>
          {currentListing?.description && <p style={{ ...S.bodyText, marginTop: 14 }}>{currentListing.description}</p>}
        </>
      ),
    },
    {
      id: "guidebook",
      label: "Guidebook",
      icon: <BookOpen size={24} strokeWidth={1.5} color={GOLD} />,
      content: (
        <>
          <p style={S.modalIntro}>Your host's personal tips and local recommendations.</p>
          {currentListing?.guidebook
            ? <p style={{ ...S.bodyText, marginTop: 14 }}>{currentListing.guidebook}</p>
            : <p style={S.empty}>No guidebook notes yet.</p>
          }
          {hasGeo && <GoldButton icon={<MapPin size={14} color={WHITE} />} onClick={() => openInGoogleMaps(lat, lng)}>View listing on Google Maps</GoldButton>}
        </>
      ),
    },
    {
      id: "checkin",
      label: "Check-In",
      icon: <LogIn size={24} strokeWidth={1.5} color={GOLD} />,
      content: <KVList obj={currentListing?.checkInMethods} />,
    },
    {
      id: "checkout",
      label: "Check-Out",
      icon: <LogOut size={24} strokeWidth={1.5} color={GOLD} />,
      content: <ArrayDetail selected={currentListing?.checkoutInstructions?.selected} details={currentListing?.checkoutInstructions?.details} />,
    },
    {
      id: "wifi",
      label: "Wi-Fi",
      icon: <Wifi size={24} strokeWidth={1.5} color={GOLD} />,
      content: currentListing?.wifi?.name || currentListing?.wifi?.password ? (
        <div style={S.wifiCard}>
          <div style={S.wifiRow}><span style={S.kvKey}>Network</span><span style={S.wifiValue}>{currentListing.wifi.name || "—"}</span></div>
          <div style={S.wifiDivider} />
          <div style={S.wifiRow}><span style={S.kvKey}>Password</span><span style={S.wifiValue}>{currentListing.wifi.password || "—"}</span></div>
        </div>
      ) : <p style={S.empty}>Not provided</p>,
    },
    {
      id: "directions",
      label: "Directions",
      icon: <Navigation size={24} strokeWidth={1.5} color={GOLD} />,
      content: (
        <>
          {currentListing?.direction ? <p style={S.bodyText}>{currentListing.direction}</p> : <p style={S.empty}>No written directions provided.</p>}
          {hasGeo && <GoldButton icon={<Navigation size={14} color={WHITE} />} onClick={() => openDirections(lat, lng)}>Get Directions via Google Maps</GoldButton>}
        </>
      ),
    },
    {
      id: "address",
      label: "Address",
      icon: <MapPin size={24} strokeWidth={1.5} color={GOLD} />,
      content: <KVList obj={currentListing?.address} lat={hasGeo ? lat : undefined} lng={hasGeo ? lng : undefined} />,
    },
    {
      id: "restaurants",
      label: "Restaurants",
      icon: <UtensilsCrossed size={24} strokeWidth={1.5} color={GOLD} />,
      content: hasGeo ? <NearbyPanel lat={lat} lng={lng} type="restaurant" label="Restaurants & Cafes" /> : <p style={S.empty}>Location not available.</p>,
    },
    {
      id: "shops",
      label: "Shops",
      icon: <ShoppingBag size={24} strokeWidth={1.5} color={GOLD} />,
      content: hasGeo ? <NearbyPanel lat={lat} lng={lng} type="supermarket" label="Supermarkets & Shops" /> : <p style={S.empty}>Location not available.</p>,
    },
    {
      id: "sightseeing",
      label: "Sightseeing",
      icon: <Eye size={24} strokeWidth={1.5} color={GOLD} />,
      content: hasGeo
        ? <NearbyPanel lat={lat} lng={lng} type="tourist_attraction" label="Tourist Attractions" keyword="popular landmark" />
        : <p style={S.empty}>Location not available.</p>,
    },
    {
      id: "parks",
      label: "Parks",
      icon: <TreePine size={24} strokeWidth={1.5} color={GOLD} />,
      content: hasGeo ? <NearbyPanel lat={lat} lng={lng} type="park" label="Parks & Nature" /> : <p style={S.empty}>Location not available.</p>,
    },
    {
      id: "rides",
      label: "Rides",
      icon: <Car size={24} strokeWidth={1.5} color={GOLD} />,
      content: hasGeo ? <RidesPanel lat={lat} lng={lng} /> : <p style={S.empty}>Location not available.</p>,
    },
    {
      id: "emergency",
      label: "Emergency",
      icon: <AlertTriangle size={24} strokeWidth={1.5} color={RED} />,
      content: hasGeo ? <EmergencyPanel lat={lat} lng={lng} /> : <p style={S.empty}>Location not available.</p>,
    },
    {
      id: "faq",
      label: "FAQ",
      icon: <HelpCircle size={24} strokeWidth={1.5} color={GOLD} />,
      content: <p style={S.empty}>No FAQs added yet. Feel free to message your host.</p>,
    },
  ];

  if (loading) return (
    <div style={S.loadingWrap}>
      <div style={S.spinner} />
      <p style={{ color: MUTED, fontSize: 14, marginTop: 16, fontFamily: "'Poppins',sans-serif" }}>Loading your guide…</p>
    </div>
  );

  return (
    <div style={S.root}>
      <style>{CSS}</style>

      {/* ── HERO ── */}
      <div style={S.hero}>
        <button style={S.backBtn} onClick={() => navigate(-1)}>
          <ArrowLeft size={16} color={GOLD} />
        </button>
        <p style={S.eyebrow}>DEAR GUEST,</p>
        <h1 style={S.heroTitle}>Welcome!</h1>
        <p style={S.heroSub}>TAP ON THE ICONS BELOW</p>
        {currentListing?.title && (
          <div style={S.listingPill}><Home size={10} color={GOLD} />{currentListing.title}</div>
        )}
      </div>

      {/* ── ICON GRID ── */}
      <div style={S.gridWrap}>
        <div style={S.grid} className="icon-grid">
          {sections.map((sec) => (
            <button key={sec.id} style={S.iconBtn} className="icon-btn" onClick={() => setActiveModal(sec)}>
              <div
                style={{
                  ...S.iconCircle,
                  ...(sec.id === "emergency" ? { borderColor: "#FCA5A5", background: "#FFF5F5" } : {}),
                }}
                className="icon-circle"
              >
                {sec.icon}
              </div>
              <span style={{
                ...S.iconLabel,
                ...(sec.id === "emergency" ? { color: RED } : {}),
              }}>{sec.label}</span>
            </button>
          ))}
        </div>
      </div>

      {/* ── CONTACT HOST ── */}
      <div style={S.contactStrip}>
        <button
          style={S.contactBtn}
          onClick={() => {
            if (!currentUser) { navigate("/AuthSwitch"); return; }
            if (currentListing?.hostId) navigate(`/messages/${currentListing.hostId}`);
          }}
        >
          <MessageCircle size={15} color={WHITE} />
          CONTACT HOST
        </button>
      </div>

      {/* ── MODAL ── */}
      {activeModal && (
        <div
          style={{
            ...S.backdrop,
            alignItems: isDesktop ? "center" : "flex-end",
            padding: isDesktop ? "24px" : 0,
          }}
          onClick={() => setActiveModal(null)}
        >
          <div
            style={{
              ...S.modalBox,
              borderRadius: isDesktop ? 24 : "24px 24px 0 0",
              ...(activeModal.id === "emergency" ? { borderTop: `3px solid ${RED}` } : {}),
            }}
            className="modal-box"
            onClick={(e) => e.stopPropagation()}
          >
            <div style={S.modalHeader}>
              <div style={{
                ...S.modalIconWrap,
                ...(activeModal.id === "emergency" ? { background: "#FEF2F2", borderColor: "#FCA5A5" } : {}),
              }}>{activeModal.icon}</div>
              <h2 style={{
                ...S.modalTitle,
                ...(activeModal.id === "emergency" ? { color: RED } : {}),
              }}>{activeModal.label}</h2>
              <button style={S.closeBtn} onClick={() => setActiveModal(null)}>
                <X size={18} color={DARK} />
              </button>
            </div>
            <div style={S.modalDivider} />
            <div style={S.modalContent}>{activeModal.content}</div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ShortChecking;

// ─── Styles ──────────────────────────────────────────────────────────────────
const S: Record<string, React.CSSProperties> = {
  root: { minHeight: "100vh", backgroundColor: CREAM, fontFamily: "'Cormorant Garamond',Georgia,serif", display: "flex", flexDirection: "column" },
  loadingWrap: { minHeight: "100vh", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", backgroundColor: CREAM },
  spinner: { width: 36, height: 36, borderRadius: "50%", border: `3px solid ${BORDER}`, borderTopColor: GOLD, animation: "spin 0.8s linear infinite" },
  hero: { padding: "50px 24px 24px", textAlign: "center", position: "relative" },
  backBtn: { position: "absolute", top: 18, left: 18, width: 36, height: 36, borderRadius: "50%", border: `1.5px solid ${BORDER}`, background: WHITE, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", boxShadow: "0 2px 8px rgba(0,0,0,0.06)" },
  eyebrow: { fontSize: 11, letterSpacing: "0.22em", color: MUTED, fontFamily: "'Poppins',sans-serif", fontWeight: 500, margin: "0 0 6px" },
  heroTitle: { fontSize: "clamp(42px,10vw,72px)", fontWeight: 700, color: GOLD, margin: "0 0 8px", lineHeight: 1, fontStyle: "italic" },
  heroSub: { fontSize: 10, letterSpacing: "0.2em", color: MUTED, fontFamily: "'Poppins',sans-serif", fontWeight: 500, margin: "0 0 12px" },
  listingPill: { display: "inline-flex", alignItems: "center", gap: 6, padding: "5px 14px", borderRadius: 50, border: `1px solid ${BORDER}`, background: WHITE, fontSize: 12, fontFamily: "'Poppins',sans-serif", color: MUTED, fontWeight: 500 },
  gridWrap: { flex: 1, padding: "10px 12px 14px", maxWidth: 960, width: "100%", margin: "0 auto", boxSizing: "border-box" as const },
  grid: { display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: "2px" },
  iconBtn: { background: "transparent", border: "none", cursor: "pointer", display: "flex", flexDirection: "column", alignItems: "center", gap: 5, padding: "10px 3px", borderRadius: 14 },
  iconCircle: { width: 62, height: 62, borderRadius: "50%", border: `1.5px solid ${BORDER}`, background: WHITE, display: "flex", alignItems: "center", justifyContent: "center", boxShadow: "0 2px 10px rgba(0,0,0,0.05)", transition: "box-shadow 0.2s,transform 0.2s" },
  iconLabel: { fontSize: 9, fontWeight: 700, letterSpacing: "0.07em", color: DARK, fontFamily: "'Poppins',sans-serif", textTransform: "uppercase" as const, textAlign: "center" as const, lineHeight: 1.3, maxWidth: 68 },
  contactStrip: { padding: "4px 18px 34px", maxWidth: 960, width: "100%", margin: "0 auto", boxSizing: "border-box" as const },
  contactBtn: { width: "100%", padding: "15px", borderRadius: 50, border: "none", background: `linear-gradient(135deg,${GOLD},#A87418)`, color: WHITE, fontSize: 12, fontWeight: 700, letterSpacing: "0.15em", fontFamily: "'Poppins',sans-serif", cursor: "pointer", boxShadow: "0 4px 20px rgba(200,148,31,0.28)", display: "flex", alignItems: "center", justifyContent: "center", gap: 8 },
  backdrop: { position: "fixed", inset: 0, backgroundColor: "rgba(28,20,16,0.54)", display: "flex", justifyContent: "center", zIndex: 1000 },
  modalBox: { backgroundColor: WHITE, width: "100%", maxWidth: 640, padding: "24px 24px 44px", boxSizing: "border-box" as const, maxHeight: "88vh", overflowY: "auto" as const, boxShadow: "0 -8px 40px rgba(0,0,0,0.14)" },
  modalHeader: { display: "flex", alignItems: "center", gap: 12 },
  modalIconWrap: { width: 46, height: 46, borderRadius: 13, background: `${GOLD}18`, border: `1.5px solid ${GOLD}33`, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 },
  modalTitle: { flex: 1, fontSize: 22, fontWeight: 700, color: DARK, margin: 0, fontFamily: "'Cormorant Garamond',Georgia,serif", fontStyle: "italic" },
  closeBtn: { width: 34, height: 34, borderRadius: "50%", border: `1px solid ${BORDER}`, background: "#F5F3EF", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 },
  modalDivider: { height: 1, background: BORDER, margin: "16px 0" },
  modalContent: { paddingBottom: 8 },
  modalIntro: { fontSize: 14, color: "#555", lineHeight: 1.8, margin: 0, fontFamily: "'Poppins',sans-serif", fontWeight: 400 },
  kvGrid: { display: "flex", flexDirection: "column", gap: 0 },
  kvRow: { display: "flex", justifyContent: "space-between", alignItems: "flex-start", padding: "11px 0", borderBottom: `1px solid ${BORDER}`, gap: 16 },
  kvKey: { fontSize: 11, fontWeight: 700, color: MUTED, textTransform: "uppercase" as const, letterSpacing: "0.08em", fontFamily: "'Poppins',sans-serif", flexShrink: 0 },
  kvVal: { fontSize: 14, color: DARK, fontFamily: "'Poppins',sans-serif", textAlign: "right" as const, fontWeight: 500 },
  goldBtn: { marginTop: 16, display: "flex", alignItems: "center", gap: 8, padding: "13px 20px", borderRadius: 50, border: "none", background: `linear-gradient(135deg,${GOLD},#A87418)`, color: WHITE, fontSize: 13, fontWeight: 700, fontFamily: "'Poppins',sans-serif", cursor: "pointer", letterSpacing: "0.05em", boxShadow: "0 4px 16px rgba(200,148,31,0.28)", width: "100%", justifyContent: "center" as const },
  outlineBtn: { marginTop: 6, display: "flex", alignItems: "center", justifyContent: "center" as const, gap: 8, padding: "11px", borderRadius: 12, border: `1.5px solid ${BORDER}`, background: WHITE, color: DARK, fontSize: 13, fontWeight: 700, fontFamily: "'Poppins',sans-serif", cursor: "pointer", width: "100%" },
  wifiCard: { background: `linear-gradient(135deg,${GOLD}12,${GOLD}05)`, border: `1.5px solid ${GOLD}33`, borderRadius: 16, padding: "16px 20px" },
  wifiRow: { display: "flex", justifyContent: "space-between", alignItems: "center", gap: 16, padding: "6px 0" },
  wifiDivider: { height: 1, background: `${GOLD}22`, margin: "6px 0" },
  wifiValue: { fontSize: 15, fontWeight: 700, color: DARK, fontFamily: "'Poppins',sans-serif", letterSpacing: "0.04em" },
  bodyText: { fontSize: 15, color: "#444", lineHeight: 1.8, fontFamily: "'Poppins',sans-serif", margin: 0 },
  empty: { color: MUTED, fontStyle: "italic", fontSize: 14, fontFamily: "'Poppins',sans-serif", margin: 0, padding: "10px 0" },
  placeCard: { display: "flex", alignItems: "center", gap: 12, padding: "10px", borderRadius: 14, border: `1.5px solid ${BORDER}`, background: WHITE, cursor: "pointer", boxShadow: "0 2px 8px rgba(0,0,0,0.04)", transition: "box-shadow 0.18s" },
  placePhoto: { width: 50, height: 50, borderRadius: 10, objectFit: "cover" as const, flexShrink: 0 },
  placePhotoFallback: { width: 50, height: 50, borderRadius: 10, background: `${GOLD}14`, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 },
  placeInfo: { flex: 1, minWidth: 0 },
  placeName: { fontSize: 14, fontWeight: 700, color: DARK, fontFamily: "'Poppins',sans-serif", whiteSpace: "nowrap" as const, overflow: "hidden", textOverflow: "ellipsis" },
  placeAddr: { fontSize: 11, color: MUTED, fontFamily: "'Poppins',sans-serif", marginTop: 2, whiteSpace: "nowrap" as const, overflow: "hidden", textOverflow: "ellipsis" },
  placeRating: { display: "flex", alignItems: "center", gap: 4, marginTop: 3, fontSize: 12, fontWeight: 700, color: GOLD, fontFamily: "'Poppins',sans-serif" },
  // Emergency
  emergencyCard: { background: "#FFF5F5", border: `1.5px solid #FCA5A5`, borderRadius: 16, padding: "16px 18px" },
  emergencyHeader: { display: "flex", alignItems: "center", gap: 8, marginBottom: 14 },
  emergencyCountry: { fontSize: 14, fontWeight: 700, color: RED, fontFamily: "'Poppins',sans-serif" },
  emergencyGrid: { display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 },
  emergencyItem: { display: "flex", alignItems: "center", gap: 10, padding: "12px", borderRadius: 12, background: WHITE, border: `1px solid #FCA5A5`, cursor: "pointer", textDecoration: "none" },
  emergencyEmoji: { fontSize: 20, flexShrink: 0 },
  emergencyLabel: { fontSize: 11, color: MUTED, fontFamily: "'Poppins',sans-serif", fontWeight: 600, textTransform: "uppercase" as const, letterSpacing: "0.06em" },
  emergencyNumber: { fontSize: 18, fontWeight: 800, color: RED, fontFamily: "'Poppins',sans-serif", letterSpacing: "0.05em" },
  sectionSubHead: { display: "flex", alignItems: "center", gap: 8, fontSize: 13, fontWeight: 700, color: DARK, fontFamily: "'Poppins',sans-serif", textTransform: "uppercase" as const, letterSpacing: "0.08em" },
  // Rides
  appsGrid: { display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 },
  appCard: { display: "flex", alignItems: "center", gap: 10, padding: "14px", borderRadius: 14, border: `1.5px solid ${BORDER}`, background: WHITE, textDecoration: "none", cursor: "pointer", transition: "box-shadow 0.18s" },
  appDot: { width: 10, height: 10, borderRadius: "50%", flexShrink: 0 },
  appName: { fontSize: 14, fontWeight: 700, color: DARK, fontFamily: "'Poppins',sans-serif", flex: 1 },
};

const CSS = `
  @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,400;0,700;1,400;1,700&family=Poppins:wght@400;500;600;700&display=swap');
  @keyframes spin { to { transform: rotate(360deg); } }
  @keyframes slideUp { from { transform: translateY(60px); opacity: 0; } to { transform: translateY(0); opacity: 1; } }
  .spin { animation: spin 1s linear infinite; }
  .icon-btn:hover .icon-circle { box-shadow: 0 6px 22px rgba(200,148,31,0.28) !important; transform: translateY(-3px) !important; }
  .icon-btn:active .icon-circle { transform: scale(0.93) !important; }
  .modal-box { animation: slideUp 0.3s cubic-bezier(0.34,1.26,0.64,1) both; }
  .modal-box::-webkit-scrollbar { width: 4px; }
  .modal-box::-webkit-scrollbar-thumb { background: #EDE8E0; border-radius: 4px; }
  @media (min-width: 480px) { .icon-grid { grid-template-columns: repeat(4,1fr) !important; gap: 6px !important; } }
  @media (min-width: 700px) { .icon-grid { grid-template-columns: repeat(7,1fr) !important; gap: 10px !important; } }
  @media (min-width: 640px) { .modal-box { border-radius: 24px !important; max-height: 82vh; } }
`;